import type { SuggestionItem } from "@digdir/designsystemet-react";
import { useEffect, useMemo, useState } from "react";
import { filterRapportRows } from "../data/rapportkatalogDataSource";
import type { ReportCatalogEntry } from "../data/types";
import {
    areaQueryParamKey,
    getDefaultSortDirectionForOption,
    ownerQueryParamKey,
    type SortOption,
    targetGroupQueryParamKey,
} from "./filterDefinitions";
import { createSuggestionOptions, filterRowsBySuggestions, sortRows } from "./filterUtils";
import {
    getInitialSearchTermFromUrl,
    getInitialSortDirectionFromUrl,
    getInitialSortOptionFromUrl,
    getInitialSuggestionValuesFromUrl,
    writeStateToUrl,
} from "./urlState";

export function useRapportkatalogFilterState(rows: ReportCatalogEntry[]) {
    const [searchTerm, setSearchTerm] = useState(() => getInitialSearchTermFromUrl());
    const [sortOption, setSortOption] = useState<SortOption>(() => getInitialSortOptionFromUrl());
    const [sortDirection, setSortDirection] = useState(() =>
        getInitialSortDirectionFromUrl(getInitialSortOptionFromUrl()),
    );
    const [selectedAreas, setSelectedAreas] = useState<SuggestionItem[]>(() =>
        getInitialSuggestionValuesFromUrl(areaQueryParamKey),
    );
    const [selectedOwners, setSelectedOwners] = useState<SuggestionItem[]>(() =>
        getInitialSuggestionValuesFromUrl(ownerQueryParamKey),
    );
    const [selectedTargetGroups, setSelectedTargetGroups] = useState<SuggestionItem[]>(() =>
        getInitialSuggestionValuesFromUrl(targetGroupQueryParamKey),
    );

    const areaOptions = useMemo(() => createSuggestionOptions(rows.map((row) => row.area)), [rows]);
    const ownerOptions = useMemo(() => createSuggestionOptions(rows.map((row) => row.owner)), [rows]);
    const targetGroupOptions = useMemo(() => createSuggestionOptions(rows.map((row) => row.targetGroups)), [rows]);

    const setSortOptionWithDefaultDirection = (nextSortOption: SortOption) => {
        setSortOption(nextSortOption);
        setSortDirection(getDefaultSortDirectionForOption(nextSortOption));
    };

    const setSortFromHeaderClick = (nextSortOption: SortOption) => {
        if (nextSortOption === sortOption) {
            setSortDirection((previousDirection) => (previousDirection === "asc" ? "desc" : "asc"));
            return;
        }

        setSortOption(nextSortOption);
        setSortDirection(getDefaultSortDirectionForOption(nextSortOption));
    };

    useEffect(() => {
        writeStateToUrl({
            selectedAreas,
            selectedOwners,
            selectedTargetGroups,
            searchTerm,
            sortOption,
            sortDirection,
        });
    }, [selectedAreas, selectedOwners, selectedTargetGroups, searchTerm, sortOption, sortDirection]);

    const visibleRows = useMemo(() => {
        const searchFilteredRows = filterRapportRows(rows, searchTerm);
        const selectedAreaValues = new Set(selectedAreas.map((item) => item.value));
        const selectedOwnerValues = new Set(selectedOwners.map((item) => item.value));
        const selectedTargetGroupValues = new Set(selectedTargetGroups.map((item) => item.value));

        const filteredRows = filterRowsBySuggestions(
            searchFilteredRows,
            selectedAreaValues,
            selectedOwnerValues,
            selectedTargetGroupValues,
        );

        return sortRows(filteredRows, sortOption, sortDirection);
    }, [rows, searchTerm, selectedAreas, selectedOwners, selectedTargetGroups, sortOption, sortDirection]);

    return {
        searchTerm,
        setSearchTerm,
        sortOption,
        sortDirection,
        setSortOptionWithDefaultDirection,
        setSortFromHeaderClick,
        selectedAreas,
        setSelectedAreas,
        selectedOwners,
        setSelectedOwners,
        selectedTargetGroups,
        setSelectedTargetGroups,
        areaOptions,
        ownerOptions,
        targetGroupOptions,
        visibleRows,
    };
}
